import React from 'react';
import { Box, Flex, Heading, IconButton, Spinner, Stack, Text } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarBlank, CaretLeft, CaretRight, Info } from 'phosphor-react';
import { useNavigate } from 'react-router-dom';

import { GlassCard } from '../../../../../components/glass';
import type { IContaCilMesStatus, MesStatus } from '../../../../../hooks/useContaCilDashboard';
import { useContaCilMesDetalhe } from '../../../../../hooks/useContaCilDashboard';
import { YearDetailPanel } from './YearDetailPanel';

const MES_PAGE_SIZE = 8;

const MESES_FULL = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const STATUS_LABEL: Record<MesStatus, string> = {
  vazio: 'Sem movimento registrado',
  parcial: 'Importado parcialmente',
  concluido: 'Mês conciliado',
  cliente: 'Aguardando conciliação',
};

const STATUS_ACCENT: Record<MesStatus, string> = {
  vazio: '#94A3B8',
  parcial: '#F59E0B',
  concluido: '#10B981',
  cliente: '#6366F1',
};

const formatNumber = (n: number): string =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(n);

interface MesDetailPanelProps {
  mes: IContaCilMesStatus | null;
  ano: number;
  // 12 meses do /dashboard/ — usado pra render o detalhe-do-ano quando nenhum
  // mês está selecionado. Se ausente, cai no EmptyPanel antigo.
  meses?: IContaCilMesStatus[];
}

const EmptyPanel = () => (
  <GlassCard intensity="medium" p={6} h="full" minH="280px">
    <Flex direction="column" align="center" justify="center" h="full" textAlign="center" gap={3}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        w="48px"
        h="48px"
        borderRadius="full"
        bg="rgba(99, 102, 241, 0.10)"
        color="#6366F1"
      >
        <CalendarBlank size={24} weight="duotone" />
      </Box>
      <Box>
        <Text fontSize="sm" fontWeight="600" color="#0F172A" mb={1}>
          Selecione um mês
        </Text>
        <Text fontSize="xs" color="#64748B" lineHeight="1.5" maxW="240px">
          Toque em qualquer bloco do calendário ao lado para ver o detalhe daquele mês.
        </Text>
      </Box>
    </Flex>
  </GlassCard>
);

export const MesDetailPanel = ({ mes, ano, meses }: MesDetailPanelProps) => {
  const navigate = useNavigate();
  const [page, setPage] = React.useState(1);
  // Reseta pra página 1 ao trocar de mês/ano.
  React.useEffect(() => {
    setPage(1);
  }, [mes?.mes, ano]);
  const { data: detalhe, isLoading: empresasLoading } = useContaCilMesDetalhe(
    ano,
    mes?.mes ?? null,
    page,
    MES_PAGE_SIZE,
  );
  const empresas = detalhe?.items ?? [];
  const totalPages = detalhe?.pages ?? 0;
  const totalEmpresas = detalhe?.total ?? 0;
  const abrirEmpresa = (id: number) =>
    navigate(`/modulesHome/accountingConciliation/conciliacao-contabil/empresas?empresa=${id}`);
  if (!mes) {
    return meses ? <YearDetailPanel meses={meses} ano={ano} /> : <EmptyPanel />;
  }

  const accent = STATUS_ACCENT[mes.status];

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={mes.mes}
        initial={{ opacity: 0, x: 16 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -16 }}
        transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
      >
        <GlassCard intensity="medium" p={6} h="full" minH="280px">
          <Stack spacing={5}>
            <Box>
              <Text
                fontSize="10px"
                fontWeight="600"
                color="#94A3B8"
                textTransform="uppercase"
                letterSpacing="0.08em"
                mb={1}
              >
                Detalhe do mês
              </Text>
              <Flex align="center" justify="space-between" gap={3}>
                <Heading
                  as="h3"
                  fontSize="xl"
                  fontWeight="700"
                  color="#0F172A"
                  letterSpacing="-0.02em"
                  lineHeight="1.1"
                >
                  {MESES_FULL[mes.mes - 1]} / {ano}
                </Heading>
                <Box
                  px={2.5}
                  py={1}
                  borderRadius="full"
                  bg={`${accent}1F`}
                  color={accent}
                  fontSize="10px"
                  fontWeight="700"
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                  whiteSpace="nowrap"
                >
                  {STATUS_LABEL[mes.status]}
                </Box>
              </Flex>
            </Box>

            {mes.total > 0 && (empresasLoading || totalEmpresas > 0) && (
              <Box borderTopWidth="1px" borderColor="rgba(15, 23, 42, 0.06)" pt={4}>
                <Flex justify="space-between" align="baseline" mb={2}>
                  <Text
                    fontSize="10px"
                    fontWeight="600"
                    color="#94A3B8"
                    textTransform="uppercase"
                    letterSpacing="0.06em"
                  >
                    Empresas do mês
                  </Text>
                  {totalEmpresas > 0 && (
                    <Text fontSize="10px" color="#94A3B8" fontWeight="600">
                      {formatNumber(totalEmpresas)}
                    </Text>
                  )}
                </Flex>
                {empresasLoading && empresas.length === 0 ? (
                  <Flex justify="center" py={2}>
                    <Spinner size="sm" color="#6366F1" />
                  </Flex>
                ) : (
                  <Stack spacing={1.5} maxH="360px" overflowY="auto" pr={1}>
                    {empresas.map(e => (
                      <Box
                        as="button"
                        key={e.empresa_id}
                        w="full"
                        textAlign="left"
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={2}
                        px={3}
                        py={2}
                        borderRadius="lg"
                        bg="rgba(15,23,42,0.02)"
                        _hover={{ bg: 'rgba(27,136,245,0.06)' }}
                        onClick={() => abrirEmpresa(e.empresa_id)}
                      >
                        <Text fontSize="12px" fontWeight="600" color="#0F172A" isTruncated>
                          {e.razao_social || `Empresa ${e.empresa_id}`}
                        </Text>
                        <Flex align="center" gap={2} flexShrink={0}>
                          {e.pendentes > 0 && (
                            <Text fontSize="11px" color="#F59E0B" fontWeight="600">
                              {e.pendentes} pend.
                            </Text>
                          )}
                          <Box color="#CBD5E1">
                            <CaretRight size={12} weight="bold" />
                          </Box>
                        </Flex>
                      </Box>
                    ))}
                  </Stack>
                )}
                {totalPages > 1 && (
                  <Flex align="center" justify="space-between" mt={3}>
                    <IconButton
                      aria-label="Página anterior"
                      icon={<CaretLeft size={14} weight="bold" />}
                      size="xs"
                      variant="ghost"
                      isDisabled={page <= 1 || empresasLoading}
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                    />
                    <Text fontSize="11px" color="#64748B" fontWeight="600">
                      {page} de {totalPages}
                    </Text>
                    <IconButton
                      aria-label="Próxima página"
                      icon={<CaretRight size={14} weight="bold" />}
                      size="xs"
                      variant="ghost"
                      isDisabled={page >= totalPages || empresasLoading}
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    />
                  </Flex>
                )}
              </Box>
            )}

            {mes.total === 0 && (
              <Flex
                align="center"
                gap={2}
                bg="rgba(99, 102, 241, 0.06)"
                p={3}
                borderRadius="lg"
                color="#6366F1"
              >
                <Info size={16} weight="duotone" />
                <Text fontSize="11px" color="#475569" lineHeight="1.4">
                  Nenhum extrato importado para este mês ainda.
                </Text>
              </Flex>
            )}
          </Stack>
        </GlassCard>
      </motion.div>
    </AnimatePresence>
  );
};
